/**
 * Shared helpers for authorisation review save: Firestore-safe stripping, dates,
 * J-code identity, and field snapshots for payloads.
 *
 * @module authorisationDetails/utils/saveReviewHelpers
 */
import moment from "moment";
import {
  AuthRangeResponse,
  JCodeDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { DocumentTypesForOrder } from "../../../../shared/enums/documentTypes";
import { PLACEHOLDER } from "./constants";

export function stripUndefined<T>(value: T): T {
  if (value === undefined) return value;
  if (value instanceof Date) return value;
  if (Array.isArray(value)) {
    return value.map(stripUndefined).filter((v) => v !== undefined) as T;
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)]),
    ) as T;
  }
  return value;
}

/** Normalizes date strings to MM/DD/YYYY for Firestore and BigQuery dump. Accepts MM-DD-YYYY (UI), YYYY-MM-DD, MM/DD/YYYY, or other values moment can parse. */
export function toStorageDate(value: string): string {
  if (!value || value === PLACEHOLDER) return value;
  const strict = moment(
    value,
    ["MM-DD-YYYY", "YYYY-MM-DD", "MM/DD/YYYY"],
    true,
  );
  if (strict.isValid()) return strict.format("MM/DD/YYYY");
  const loose = moment(value);
  return loose.isValid() ? loose.format("MM/DD/YYYY") : value;
}

/**
 * Stable identifier for a J-code row (`id` preferred, else `j_code`).
 *
 * @param j - J-code document from auth-on-file
 * @returns Non-empty id string when possible
 */
export const getJCodeId = (j: JCodeDetails) => j.id ?? j.j_code ?? "";

/**
 * Resolves the auth-letter storage path from documents or nested auth-letter data.
 *
 * @param jCode - J-code with optional `documents` and `auth_letter`
 * @returns Document path string, or empty string when none
 */
export function getAuthLetterPathFromJCode(jCode: JCodeDetails): string {
  return (
    jCode.documents?.find(
      (d) => d.doc_type === DocumentTypesForOrder.AUTH_LETTER,
    )?.doc_path ??
    jCode.auth_letter?.auth_letter_path ??
    ""
  );
}

/**
 * Num visits for authorisation UI and payloads — `auth_letter.calculated_visits` only.
 *
 * @param j - J-code row from auth-on-file
 * @returns Visit count string, or empty when auth letter has no calculated visits
 */
export function getAuthLetterNumVisitsAsString(j: JCodeDetails): string {
  const raw = j.auth_letter?.calculated_visits;
  if (raw == null || raw === "") return "";
  return String(raw);
}

/**
 * Num visits formatted for procedure fields (matches units-approved placeholder behaviour).
 *
 * @param j - J-code row from auth-on-file
 * @returns Display string or {@link PLACEHOLDER} when auth letter has no visits
 */
export function formatAuthLetterNumVisitsForDisplay(j: JCodeDetails): string {
  const value = getAuthLetterNumVisitsAsString(j);
  return value === "" ? PLACEHOLDER : value;
}

/**
 * Resolves Num Visits form input for auth_letter persistence.
 * Placeholder keeps the existing value; cleared/whitespace becomes null; numeric strings become numbers.
 *
 * @param formValue - Raw UI string (may be {@link PLACEHOLDER})
 * @param existingValue - Current `auth_letter.calculated_visits` when the field was not edited
 * @returns Value to write on `auth_letter.calculated_visits`
 */
export function resolveNumVisitsFromFormState(
  formValue: string,
  existingValue?: number | string | null,
): number | string | null | undefined {
  if (formValue === PLACEHOLDER) {
    return existingValue;
  }
  const trimmed = formValue.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? formValue : parsed;
}

/**
 * Same rules as {@link resolveNumVisitsFromFormState}, serialized for the auth letter review dump API
 * (`num_visits` key; backend persists to `calculated_visits`).
 *
 * @param formValue - Raw UI string (may be {@link PLACEHOLDER})
 * @param existingValue - Current `auth_letter.calculated_visits` when the field was not edited
 * @returns String visit count or null when cleared / absent
 */
export function resolveNumVisitsForDump(
  formValue: string,
  existingValue?: number | string | null,
): string | null {
  const resolved = resolveNumVisitsFromFormState(formValue, existingValue);
  if (resolved == null || resolved === "") return null;
  return String(resolved);
}

/** Collects all J-codes from entries whose id is in jCodeIds. */
export function getJCodesByIds(
  authEntries: AuthRangeResponse[],
  jCodeIds: string[],
): JCodeDetails[] {
  const idSet = new Set(jCodeIds);
  return authEntries
    .flatMap((e) => e.j_codes ?? [])
    .filter((j) => idSet.has(getJCodeId(j)));
}

/**
 * Procedure-relevant fields from a J-code for diffing and review-dump payloads.
 *
 * @param j - J-code row from auth-on-file
 * @returns Plain object of normalised procedure / auth-letter fields
 */
export function getProcedureFields(j: JCodeDetails) {
  const a = j.auth_letter ?? {};
  return {
    j_code: j.j_code ?? "",
    auth_status: a.auth_letter_auth_status ?? j.auth_status ?? "",
    start_date: toStorageDate(a.auth_letter_start_date ?? j.start_date ?? ""),
    end_date: toStorageDate(a.auth_letter_end_date ?? j.end_date ?? ""),
    num_visits: getAuthLetterNumVisitsAsString(j),
    dosage: a.auth_letter_dosage ?? "",
    description: j.medicine_name ?? j.description ?? "",
    medicine_name: (Array.isArray(j.medicine_name) ? j.medicine_name : []).join(
      "|",
    ),
    units_approved: a.units_approved ?? "",
    notes: a.auth_letter_notes ?? "",
  };
}
